import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AuthService } from '../shared/services/auth.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  loginForm = new FormGroup({
    username: new FormControl('', Validators.required),
    password: new FormControl('', Validators.required)
  });
  loginFormSubmitted = false;
  horizontalPosition: MatSnackBarHorizontalPosition = 'end';
  verticalPosition: MatSnackBarVerticalPosition = 'top';
  resetPasswordUrl: string = environment.RESET_PASSWORD;
  private readonly _destroying$ = new Subject<null>();
  loginStatus = false;
  loginLoader: string = 'true';

  constructor(
    private authService: AuthService,
    private router: Router,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {};

  ngOnInit() {
    if(this.authService.isMsalLoggedIn()) {
      sessionStorage.setItem('isAuthLoading', 'true');
    }
    else {
      if(!sessionStorage.getItem('isAuthLoading')){
        sessionStorage.setItem('isAuthLoading', 'false');
      }
      this.loginLoader = sessionStorage.getItem('isAuthLoading')!;
    }

    if(this.authService.isUserLoggedIn$.getValue()) {
      this.router.navigate(['/home']);
      // SSO Apps Portal handoff: already authenticated via the #session fragment — skip the MSAL
      // flow below, which would otherwise navigate back to /login (this tab has no MSAL account).
      return;
    }

    this.msalService.handleRedirectObservable().pipe(takeUntil(this._destroying$)).subscribe({
      next: (result) => {
        if (!result) {
          this.setSessionStorageAndLoader("false");
        }
      },
      error: () => {
        this.setSessionStorageAndLoader("false");
      }
    });

    this.msalBroadcastService.msalSubject$
    .pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
      takeUntil(this._destroying$)
    )
    .subscribe({
      next: (result: EventMessage) => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          this.router.navigate(['/login']);
        } else {
          this.setLoginDisplay();
        }
      },
      error: () => {
        this.setSessionStorageAndLoader("false");
      }
    });

    this.msalBroadcastService.inProgress$
    .pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this._destroying$)
    )
    .subscribe({
      next: () => {
        this.setLoginDisplay();
      },
      error: () => {
        this.setSessionStorageAndLoader("false");
      }
    });
  }

  onSubmit(){
    if(this.loginForm.valid) {
      this.loginFormSubmitted = true;
      this.authService.login({
        username: this.loginForm.value['username']!,
        password: this.loginForm.value['password']!
      })
      .subscribe({
        next: () => {
          this.authService.isUserLoggedIn$.next(true);
          this.router.navigate(['/home']);
        },
        error: () => {
          this.loginFormSubmitted = false;
        }
      });
    }
  }

  onSsoLogin() {
    this.setSessionStorageAndLoader("true");
    // loginSso() redirects and doesn't throw synchronous errors
    this.authService.loginSso();
  }

  async setLoginDisplay() {

    this.loginStatus = this.msalService.instance.getAllAccounts().length > 0;

    if(this.loginStatus){
      const profile_data = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(profile_data);

      if (!this.authService.isMsalLoggedIn()) {
        this.setMsalToken();
      }
      else {
        this.loginUsingSso();
      }
    } else {
      if(this.authService.isMsalLoggedIn()) {
        await this.onSsoLogin();

        this.setSessionStorageAndLoader("false");
      }
      this.router.navigate(['/login']);
    }
  }

  setMsalToken() {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      console.error('No MSAL accounts found');
      this.setSessionStorageAndLoader("false");
      return;
    }

    const tokenRequest = {
        scopes: ['user.read'],
        account: accounts[0]
    }

    this.msalService.acquireTokenSilent(tokenRequest).subscribe({
      next: (tokenResponse) => {
        if (!tokenResponse || !tokenResponse.accessToken || !tokenResponse.idToken) {
          console.error('Invalid token response from MSAL');
          this.setSessionStorageAndLoader("false");
          return;
        }
        
        const expiry = new Date((new Date()).getTime() + 3600 * 1000);
        
        // FIX: Store MSAL tokens in sessionStorage instead of cookies
        // This prevents RequestHeaderSectionTooLarge error on hard refresh
        // sessionStorage data is NOT sent as HTTP headers, avoiding CloudFront's 8KB limit
        sessionStorage.setItem(
          'msal-access-token', 
          JSON.stringify({token: tokenResponse.accessToken, expiry: expiry})
        );
        sessionStorage.setItem(
          'msal-id-token', 
          JSON.stringify({token: tokenResponse.idToken, expiry: expiry})
        );

        this.loginUsingSso();
      },
      error: (error) => {
        console.error('Failed to acquire MSAL tokens:', error);
        this.setSessionStorageAndLoader("false");
      }
    });
  }

  loginUsingSso() {
    // FIX: Read MSAL tokens from sessionStorage instead of cookies
    const msalAccessToken = sessionStorage.getItem('msal-access-token');
    const msalIdToken = sessionStorage.getItem('msal-id-token');
    
    if (!msalAccessToken || !msalIdToken) {
      console.error('MSAL tokens not found in sessionStorage');
      this.setSessionStorageAndLoader("false");
      return;
    }
    
    try {
      const accessTokenData = JSON.parse(msalAccessToken);
      const idTokenData = JSON.parse(msalIdToken);
      
      if (!accessTokenData.token || !idTokenData.token) {
        console.error('Invalid MSAL token format');
        this.setSessionStorageAndLoader("false");
        return;
      }
      
      const activeAccount = this.msalService.instance.getActiveAccount();
      if (!activeAccount || !activeAccount.username) {
        console.error('No active account or username found');
        this.setSessionStorageAndLoader("false");
        return;
      }
      
      this.authService.loginUsingSso({
        accessToken: accessTokenData.token,
        idToken: idTokenData.token,
        email: activeAccount.username
      }).subscribe({
        next: () => {
          this.authService.isUserLoggedIn$.next(true);
          this.setSessionStorageAndLoader("false");
          this.router.navigate(['/home']);
        },
        error: (error) => {
          console.error('Login using SSO failed:', error);
          this.setSessionStorageAndLoader("false");
        }
      });
    } catch (error) {
      console.error('Failed to parse MSAL tokens:', error);
      this.setSessionStorageAndLoader("false");
    }
  }

  private setSessionStorageAndLoader(value: string) {
    this.loginLoader = value;
    sessionStorage.setItem('isAuthLoading', value);
  }

  ngOnDestroy(): void {
    this._destroying$.next(null);
    this._destroying$.complete();
  }
}
